import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const KIND_LABELS = { password: "Email and password", google: "Google sign in" };

const PREF_LABELS = [
  ["allocation", "Allocation and capital calls"],
  ["distribution", "Distributions paid"],
  ["document", "New documents issued"],
  ["statement", "Quarterly statements"],
  ["interest_recap", "Weekly interest recap"],
  ["login_alert", "New device sign in alerts"],
];

const OS_MATCHERS = [[/Windows/, "Windows"], [/Mac OS X|Macintosh/, "macOS"], [/Android/, "Android"], [/iPhone|iPad|iOS/, "iOS"], [/Linux/, "Linux"]];
const BROWSER_MATCHERS = [[/Edg/, "Edge"], [/Chrome/, "Chrome"], [/Firefox/, "Firefox"], [/Safari/, "Safari"]];
const matchLabel = (ua, matchers, fallback) => (matchers.find(([re]) => re.test(ua)) || [null, fallback])[1];

function deviceLabel(ua) {
  if (!ua) return "Unknown device";
  const os = matchLabel(ua, OS_MATCHERS, "");
  const browser = matchLabel(ua, BROWSER_MATCHERS, "Browser");
  return [browser, os].filter(Boolean).join(", ") || "Browser";
}

export default function Account() {
  const { user, setUser } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [googleCfg, setGoogleCfg] = useState({ enabled: false, client_id: "" });

  useEffect(() => { api.get("/auth/google/config").then((r) => setGoogleCfg(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ep = user?.email_prefs || {};
    setPrefs(Object.fromEntries(PREF_LABELS.map(([k]) => [k, ep[k] !== false])));
  }, [user]);

  const togglePref = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));
  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const { data } = await api.put("/auth/email-preferences", prefs);
      setUser(data);
      toast.success("Email preferences saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingPrefs(false); }
  };

  const connectGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (!googleCfg.enabled || !googleCfg.client_id) { toast.error("Google sign in is not configured yet."); return; }
    const redirectUri = window.location.origin + "/auth/google";
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem("g_oauth_state", state);
    sessionStorage.setItem("g_oauth_mode", "link");
    const p = new URLSearchParams({
      client_id: googleCfg.client_id, redirect_uri: redirectUri, response_type: "code",
      scope: "openid email profile", state, prompt: "select_account", login_hint: user?.email || "", access_type: "online",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${p.toString()}`;
  };

  const disconnectGoogle = async () => {
    try {
      const { data } = await api.post("/auth/google/unlink");
      setUser(data);
      toast.success("Google disconnected.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = () => api.get("/auth/sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const revoke = async (s) => {
    try {
      await api.post(`/auth/sessions/${s.session_id}/revoke`);
      toast.success("That session has been signed out.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const signOutOthers = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/logout-all");
      toast.success(data.revoked
        ? `Signed out of ${data.revoked} other session${data.revoked === 1 ? "" : "s"}.`
        : "No other sessions were active.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!sessions) return <span className="t-label">Loading</span>;
  const others = sessions.filter((s) => !s.current).length;

  const rows = [
    ["Name", user?.name],
    ["Email", user?.email],
    ["Entity", user?.preferences?.entity_name || "Not set"],
  ];

  return (
    <div className="rise" data-testid="account-page">
      <span className="t-label">Account</span>
      <h1 className="t-h1 text-bone mt-3">Account and security</h1>

      <div className="mt-10 max-w-xl">
        <span className="t-label">Your details</span>
        <div className="mt-3 space-y-0">
          {rows.map(([k, v]) => (
            <div key={k} className="hairline py-3 flex gap-8">
              <span className="t-label w-40 whitespace-nowrap">{k}</span>
              <span className="t-small text-bone">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-14 max-w-xl" data-testid="signin-methods">
        <span className="t-label">Sign in methods</span>
        <p className="t-small text-muted-s mt-2 measure">How you can access your account. Keep at least one method active.</p>
        <div className="mt-4 space-y-0">
          <div className="hairline py-4 flex items-center justify-between gap-6" data-testid="method-password">
            <div>
              <span className="t-small text-bone">Email and password</span>
              <p className="t-cap text-muted-s mt-1">{user?.has_password ? "Active" : "Not set"}</p>
            </div>
            <span className="t-cap text-muted-s">{user?.has_password ? "Active" : "Not set"}</span>
          </div>
          <div className="hairline py-4 flex items-center justify-between gap-6" data-testid="method-google">
            <div>
              <span className="t-small text-bone">Google</span>
              <p className="t-cap text-muted-s mt-1">{user?.google_linked ? "Connected" : "Not connected"}</p>
            </div>
            {user?.google_linked ? (
              <button onClick={disconnectGoogle} disabled={!user?.has_password} className="t-label hover:!text-[#E8E6E1] disabled:opacity-40 disabled:cursor-not-allowed" data-testid="disconnect-google-button">Disconnect</button>
            ) : (
              <button onClick={connectGoogle} className="t-label hover:!text-[#E8E6E1]" data-testid="connect-google-button">Connect</button>
            )}
          </div>
        </div>
        {user?.google_linked && !user?.has_password && (
          <p className="t-cap text-muted-s mt-3" data-testid="google-unlink-note">Set a password before disconnecting Google, or you will be locked out.</p>
        )}
      </div>

      <div className="mt-14">
        <div className="flex items-baseline justify-between gap-6 flex-wrap">
          <div>
            <span className="t-label">Active sessions</span>
            <p className="t-small text-muted-s mt-2 measure">
              Every device where you are signed in. If you see one you do not recognize, sign it out.
            </p>
          </div>
          <button onClick={signOutOthers} disabled={busy || others === 0} className="btn-ghost" data-testid="signout-others-button">
            Sign out of all other sessions
          </button>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="table-solera" data-testid="sessions-table">
            <thead>
              <tr><th>Device</th><th>Method</th><th>IP</th><th>Last active</th><th></th></tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.session_id} data-testid={`session-row-${s.session_id}`}>
                  <td className="text-bone">
                    {deviceLabel(s.user_agent)}
                    {s.current && <span className="t-cap text-muted-s ml-3">This device</span>}
                  </td>
                  <td className="text-muted-s">{KIND_LABELS[s.kind] || s.kind}</td>
                  <td className="text-muted-s t-small fig">{s.ip || "unknown"}</td>
                  <td className="text-muted-s t-small fig">{s.last_seen_at?.slice(0, 16).replace("T", " ")} UTC</td>
                  <td className="text-right">
                    {s.current ? (
                      <span className="t-cap text-muted-s">Current</span>
                    ) : (
                      <button onClick={() => revoke(s)} className="t-label hover:!text-[#E8E6E1]" data-testid={`revoke-session-${s.session_id}`}>Sign out</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-14">
        <span className="t-label">Email preferences</span>
        <p className="t-small text-muted-s mt-2 measure">
          Choose which notices reach your inbox. You will always see every notice inside the platform, and account security emails such as password resets are always sent.
        </p>
        <div className="mt-5 max-w-xl">
          {prefs && PREF_LABELS.map(([k, label]) => (
            <label key={k} className="hairline py-3 flex items-center justify-between gap-6 cursor-pointer" data-testid={`email-pref-${k}`}>
              <span className="t-small text-bone">{label}</span>
              <input type="checkbox" checked={!!prefs[k]} onChange={() => togglePref(k)} className="w-4 h-4" data-testid={`email-pref-toggle-${k}`} />
            </label>
          ))}
          <button onClick={savePrefs} disabled={savingPrefs} className="btn-primary mt-5" data-testid="save-email-prefs-button">
            {savingPrefs ? "Saving" : "Save email preferences"}
          </button>
        </div>
      </div>
    </div>
  );
}
